'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Loader2, Mail } from 'lucide-react';

interface PlanInterestFormData {
  name: string;
  email: string;
  company: string;
  docType: 'CPF' | 'CNPJ';
  docNumber: string;
}

interface Props {
  plan: 'essential' | 'professional';
  planLabel: string;
  onClose: () => void;
}

const PLAN_LABELS: Record<string, string> = {
  essential: 'RD2Sign Essential',
  professional: 'RD2Sign Professional',
};

function validateCPF(cpf: string): boolean {
  const c = cpf.replace(/\D/g, '');
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(c[i]) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== parseInt(c[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(c[i]) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === parseInt(c[10]);
}

function validateCNPJ(cnpj: string): boolean {
  const c = cnpj.replace(/\D/g, '');
  if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
  const calc = (s: string, w: number[]) => {
    let sum = 0;
    for (let i = 0; i < w.length; i++) sum += parseInt(s[i]) * w[i];
    let r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  return calc(c, w1) === parseInt(c[12]) && calc(c, w2) === parseInt(c[13]);
}

export default function PlanInterestModal({ plan, planLabel, onClose }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<PlanInterestFormData>({
    defaultValues: { docType: 'CPF' },
  });

  const docType = watch('docType');

  const inputClass = (hasError: boolean) =>
    `w-full px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
    focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
    ${hasError ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`;

  const validateDoc = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (docType === 'CPF') return validateCPF(cleaned) || 'CPF inválido';
    return validateCNPJ(cleaned) || 'CNPJ inválido';
  };

  const onSubmit = async (data: PlanInterestFormData) => {
    setIsLoading(true);
    setServerError('');
    try {
      const res = await fetch('/api/plan-interest/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company,
          docType: data.docType,
          docNumber: data.docNumber,
          plan,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        if (json.alreadyConfirmed && json.token) {
          window.location.href = `/plano-confirmacao?token=${json.token}`;
          return;
        }
        setServerError(json.error || 'Erro ao enviar. Tente novamente.');
        return;
      }
      setSubmittedEmail(data.email);
      setSubmitted(true);
    } catch {
      setServerError('Erro de conexão. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#0a1628] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white">Obter maiores informações</h2>
            <p className="text-white/40 text-xs mt-0.5">Plano: <span className="text-[#60a5fa]">{PLAN_LABELS[plan] || planLabel}</span></p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6">
          {submitted ? (
            /* Success state */
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-[#0d9488]/20 rounded-full flex items-center justify-center mx-auto mb-5">
                <Mail size={28} className="text-[#34d399]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Verifique seu e-mail</h3>
              <p className="text-white/55 text-sm leading-relaxed mb-2">
                Enviamos um e-mail de confirmação para:
              </p>
              <p className="text-[#60a5fa] font-semibold text-sm mb-5">{submittedEmail}</p>
              <p className="text-white/45 text-xs leading-relaxed mb-6">
                Clique no link do e-mail para confirmar e visualizar o valor e todos os detalhes do plano <strong className="text-white/70">{PLAN_LABELS[plan]}</strong>.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl border border-white/20 transition-all text-sm"
              >
                Fechar
              </button>
            </div>
          ) : (
            /* Form */
            <>
              <p className="text-white/50 text-sm mb-5 leading-relaxed">
                Preencha seus dados para receber um e-mail de confirmação. Após confirmar, você poderá visualizar o valor e todos os detalhes do plano selecionado.
              </p>

              {serverError && (
                <div className="mb-4 px-4 py-3 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {serverError}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-[13px] font-semibold text-white/80 mb-1.5">Nome completo</label>
                  <input
                    type="text"
                    placeholder="Seu nome completo"
                    {...register('name', { required: 'Nome é obrigatório', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
                    className={inputClass(!!errors.name)}
                  />
                  {errors.name && <p className="text-red-400 text-[12px] mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-white/80 mb-1.5">E-mail</label>
                  <input
                    type="email"
                    placeholder="seu@empresa.com.br"
                    {...register('email', {
                      required: 'E-mail é obrigatório',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'E-mail inválido' },
                    })}
                    className={inputClass(!!errors.email)}
                  />
                  {errors.email && <p className="text-red-400 text-[12px] mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-white/80 mb-1.5">Empresa / Organização</label>
                  <input
                    type="text"
                    placeholder="Nome da sua empresa"
                    {...register('company', { required: 'Empresa é obrigatória' })}
                    className={inputClass(!!errors.company)}
                  />
                  {errors.company && <p className="text-red-400 text-[12px] mt-1">{errors.company.message}</p>}
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-white/80 mb-1.5">Documento</label>
                  <div className="flex gap-2">
                    <select
                      {...register('docType')}
                      className="px-3 py-2.5 rounded-lg border border-white/10 bg-white/5 text-white text-[14px] outline-none focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db] w-24 shrink-0"
                    >
                      <option value="CPF" className="bg-[#0a1628] text-white">CPF</option>
                      <option value="CNPJ" className="bg-[#0a1628] text-white">CNPJ</option>
                    </select>
                    <input
                      type="text"
                      placeholder={docType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                      {...register('docNumber', {
                        required: `${docType} é obrigatório`,
                        validate: validateDoc,
                      })}
                      className={`flex-1 px-3.5 py-2.5 rounded-lg border text-[14px] bg-white/5 text-white placeholder-white/30 transition-colors outline-none
                        focus:ring-2 focus:ring-[#1a56db]/40 focus:border-[#1a56db]
                        ${errors.docNumber ? 'border-red-500/60 bg-red-900/10' : 'border-white/10'}`}
                    />
                  </div>
                  {errors.docNumber && <p className="text-red-400 text-[12px] mt-1">{errors.docNumber.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 bg-[#1a56db] hover:bg-[#1e40af] text-white rounded-lg font-semibold text-[14px]
                    shadow-lg shadow-[#1a56db]/30 hover:shadow-[#1a56db]/50
                    active:scale-[0.98] transition-all duration-150
                    disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                >
                  {isLoading ? (
                    <><Loader2 size={16} className="animate-spin" />Enviando...</>
                  ) : 'Enviar e-mail de confirmação'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
